<template>
    <div>
        <template v-if="loaded">
            <rkm-form
                ref="Form"
                show-legend
            >
                <rkm-row>
                    <rkm-col>
                        <rkm-form-select
                            :options="form.motivos"
                            :validations="validations.motivo"
                            label="Motivo"
                            show-default-option
                            textField="descricao"
                            v-model="model.motivoId"
                            valueField="id"
                        />
                    </rkm-col>

                    <rkm-col
                        v-if="isConsumoInterno"
                        cols="6"
                    >
                        <rkm-form-input-autocomplete
                            :repository="repositories.usuario"
                            :request-query-string-params="autocompleteQueryStringParams.usuarioSolicitante"
                            :search-fields="autocompleteFields.usuarioSolicitante"
                            :validations="validations.usuarioSolicitante"
                            label="Usuário solicitante"
                            uppercase
                            text-field="cidadaoNome"
                            v-model="model.usuarioSolicitante"
                        />
                    </rkm-col>
                </rkm-row>

                <rkm-row v-if="precisaJustificar">
                    <rkm-col>
                        <rkm-form-input
                            :validations="validations.justificativa"
                            label="Justificativa"
                            uppercase
                            v-model="model.justificativa"
                        />
                    </rkm-col>
                </rkm-row>

                <rkm-row>
                    <rkm-col cols="6">
                        <rkm-form-input
                            :value="model.estoque"
                            disabled
                            label="Estoque"
                        >
                            <rkm-input-group-append slot="append">
                                <rkm-button
                                    @click.stop="loadEstoque"
                                    color="primary"
                                    title="Atualizar estoque"
                                    v-rkm-tooltip.hover
                                >
                                    <rkm-icon icon="sync-alt" />
                                </rkm-button>
                            </rkm-input-group-append>
                        </rkm-form-input>
                    </rkm-col>

                    <rkm-col cols="6">
                        <rkm-form-input
                            :disabled="!temEstoque"
                            :validations="validations.quantidade"
                            label="Quantidade"
                            v-model="model.quantidade"
                            type="number"
                        >
                            <rkm-input-group-append
                                v-if="form.possuiEntradaComLote"
                                slot="append"
                            >
                                <rkm-button
                                    :disabled="!quantidadeValida"
                                    @click.stop="$refs.ModalLote.open(model.entradaItens)"
                                    title="Visualizar lotes"
                                    v-rkm-tooltip.hover
                                >
                                    <rkm-icon icon="box-open" />
                                </rkm-button>
                            </rkm-input-group-append>
                        </rkm-form-input>
                    </rkm-col>
                </rkm-row>
            </rkm-form>

            <ModalLote
                :model="model"
                ref="ModalLote"
            />
        </template>
    </div>
</template>

<script>
// Components.
import ModalLote from '@/components/almoxarifado/components/lote/Modal'

// Vuex.
import { mapActions } from 'vuex'
import { mapGetters } from 'vuex'

// Mixins.
import { repository, error, stringCase, object, data, file } from '@/mixins'

// Enums
import motivoBaixaTipo from '@/enums/almoxarifado/motivoBaixaTipo'

export default {
    components: { ModalLote },
    computed: {
        ...mapGetters({ lotacao: 'getLotacao' }),
        autocompleteFields() {

            return {
                usuarioSolicitante: [{ key: 'cadastro_cidadao.nome', searchable: true }],
            }
        },
        autocompleteQueryStringParams() {

            return {
                usuarioSolicitante: {
                    infraestrutura_id: this.lotacao.infraestrutura.id,
                },
            }
        },
        validations() {

            return {
                motivo: {
                    required: true,
                },
                justificativa: {
                    required: this.precisaJustificar,
                    max: 100,
                },
                usuarioSolicitante: {
                    required: this.isConsumoInterno
                },
                quantidade: {
                    required: true,
                    min_value: 1,
                    max_value: this.model.estoque,
                },
            }
        },
        precisaJustificar() {

            if (this.model && this.model.motivoId) {

                return !!this.form.motivos.find(m => m.id === this.model.motivoId).justificar
            }

            return false
        },
        isConsumoInterno() {

            if (this.model && this.model.motivoId) {

                return this.form.motivos
                    .find(m => m.id === this.model.motivoId).tipoId === this.enums.motivoBaixaTipo.CONSUMO_INTERNO
            }

            return false
        },
        temEstoque() {

            return this.model && this.model.estoque > 0
        },
        quantidadeValida() {

            if (!this.model.quantidade) {

                return false
            }

            const quantidade = parseInt(this.model.quantidade)

            return quantidade > 0 && quantidade <= this.model.estoque
        },
    },
    created() {

        this.repositories = {
            usuario: this.getConectaSusRepository('cadastro.sujeitoAtencao.usuario'),
            baixa: this.getConectaSusRepository('almoxarifado.baixa'),
            material: this.getConectaSusRepository('cadastro.material'),
            impressao: this.getConectaSusRepository('impressao.almoxarifado'),
        }

        this.load()
    },
    data: () => ({
        form: null,
        loaded: false,
        model: null,
        enums: { motivoBaixaTipo },
    }),
    methods: {
        ...mapActions(['setLoading', 'unsetLoading']),
        load() {

            this.setLoading()

            this.repositories.baixa.create(this.material.id)
                .then(({ data }) => {

                    this.form = data.form
                    this.model = data.model
                    this.loaded = true
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        save(imprimir = false) {

            this.$refs.Form.validate()
                .then(valid => {

                    if (!valid) return

                    this.setLoading()

                    this.repositories.baixa.store(this.prepareData())
                        .then(({ data }) => {

                            this.showSaveNotification()

                            if (imprimir) {

                                this.print(data.id).then(() => this.$emit('save', data))
                            }
                            else {

                                this.$emit('save', data)
                            }
                        })
                        .catch(this.handleError)
                        .finally(() => this.unsetLoading())
                })
        },
        prepareData() {

            const data = this.clone(this.model)

            delete data.estoque

            data.materialId = this.material.id
            data.quantidade = parseInt(data.quantidade)

            this.createIdKeysToObject(data, ['usuarioSolicitante'])

            data.entradaItens = data.entradaItens
                .filter(i => i.quantidadeSaida > 0)
                .map(item => ({
                    entradaItemId: item.entradaItemId,
                    quantidadeSaida: isNaN(parseInt(item.quantidadeSaida)) ? 0 : parseInt(item.quantidadeSaida),
                }))

            return this.camelObjectKeysToSnakeRecursive(data)
        },
        print(saidaId) {

            this.setLoading()

            return this.repositories.impressao.baixa(saidaId)
                .then(response => {

                    this.openFile(response.data, response.headers['content-type'])
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        loadEstoque() {

            this.form.possuiEntradaComLote = false
            this.model.estoque = 0
            this.model.entradaItens = []
            this.model.escolheuEntradaItemManualmente = false
            this.model.quantidade = null

            this.setLoading()

            this.repositories.material.getEstoque(this.material.id)
                .then(({ data }) => {

                    this.form.possuiEntradaComLote = data.possuiEntradaComLote

                    this.model.estoque = data.total

                    this.model.entradaItens = data.porEntrada.map(estoqueEntrada => {

                        estoqueEntrada.quantidadeSaida = 0

                        return estoqueEntrada
                    })
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        selectEntradaItens() {

            this.model.escolheuEntradaItemManualmente = false

            this.model.entradaItens.map(e => e.quantidadeSaida = 0)

            let quantidade = parseInt(this.model.quantidade)
            let i = 0

            while (quantidade > 0) {

                const entradaItem = this.model.entradaItens[i]
                const estoque = parseInt(entradaItem.estoque)

                if (quantidade > estoque) {

                    entradaItem.quantidadeSaida = estoque
                    quantidade -= estoque
                }
                else {

                    entradaItem.quantidadeSaida = quantidade
                    quantidade = 0
                }

                i++
            }
        }
    },
    mixins: [repository, error, stringCase, object, data, file],
    props: {
        material: { type: Object, required: true },
    },
    watch: {
        'precisaJustificar': function (precisa) {

            if (!precisa) {

                this.model.justificativa = null
            }
        },
        'isConsumoInterno': function (isConsumoInterno) {

            if (isConsumoInterno) {

                this.model.usuarioSolicitante = {
                    id: this.lotacao.usuario.id,
                    cidadaoNome: this.lotacao.usuario.cidadao.nome,
                }
            }
            else {

                this.model.usuarioSolicitante = null
            }
        },
        'model.quantidade': function () {

            if (this.form.possuiEntradaComLote && this.quantidadeValida) {

                this.selectEntradaItens()
            }
        },
        'temEstoque': function (temEstoque) {

            this.$emit('has-estoque', temEstoque)
        }
    }
}
</script>